import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import ArticlePlaceholder from "./ArticlePlaceholder";
import { loadingHints } from "./loadingHints";
import { COVER_RATIO } from "../../core/content";

/**
 * A cover photo in its type's frame (journal 3:2, impact 16:9).
 *
 * The frame holds its ratio before the image arrives, so nothing jumps; a
 * missing or failed image falls back to the generated placeholder, never to
 * an empty box. `priority` is for the one cover that is the page's largest
 * paint (article banners); everything else loads lazily. `tag` is accepted
 * for existing callers but no longer rendered: the placeholder is decorative.
 */
const Cover = ({
	src,
	alt,
	type,
	org,
	tag,
	priority = false,
	dark = false,
	className = "",
	imgClassName = "",
}) => {
	const [failed, setFailed] = useState(false);
	useEffect(() => {
		setFailed(false);
	}, [src]);

	return (
		<div
			className={`relative overflow-hidden bg-pp-surface ${className}`}
			style={{ aspectRatio: COVER_RATIO[type] || COVER_RATIO.impact }}
		>
			{src && !failed ? (
				<img
					src={src}
					alt={alt || ""}
					{...loadingHints(priority)}
					decoding="async"
					onError={() => setFailed(true)}
					className={`absolute inset-0 w-full h-full object-cover ${imgClassName}`}
				/>
			) : (
				<div className="absolute inset-0" aria-hidden="true">
					<ArticlePlaceholder org={org} dark={dark} />
				</div>
			)}
		</div>
	);
};

Cover.propTypes = {
	src: PropTypes.string,
	alt: PropTypes.string,
	type: PropTypes.oneOf(["journal", "impact"]).isRequired,
	org: PropTypes.string,
	/** Ignored; kept so callers that still pass it don't break. */
	tag: PropTypes.string,
	priority: PropTypes.bool,
	dark: PropTypes.bool,
	className: PropTypes.string,
	imgClassName: PropTypes.string,
};

export default Cover;
